/**
 * SpeedRun i18n — DOM runtime. Holds the active-language state, persists the pick
 * to localStorage, reflects it onto `<html lang>`, and exposes `t()` for the UI.
 *
 * The catalog + pure helpers live in ./messages (node-testable, no DOM). This file
 * is the thin browser wrapper. Language is resolved ONCE at module load; switching
 * language persists then reloads the page (the menu is world-free → instant + total).
 */
import { resolveLang, translateWith, type Lang, type StringKey } from './messages';

export { SUPPORTED } from './messages';
export type { Lang, StringKey } from './messages';

const STORAGE_KEY = 'maker.lang.v1';

function readStored(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function readNavLangs(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages && navigator.languages.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

let current: Lang = resolveLang(readStored(), readNavLangs());

function applyHtmlLang(): void {
  if (typeof document !== 'undefined') document.documentElement.lang = current;
}
applyHtmlLang(); // reflect the resolved language onto the document at boot (a11y)

/** The active language code. */
export function getLang(): Lang { return current; }

/** Persist a new language and reload so every string re-renders in it. */
export function setLang(l: Lang): void {
  current = l;
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* private mode — session-only */ }
  applyHtmlLang();
}

/** Translate `key` in the active language, interpolating any `{name}` params. */
export function t(key: StringKey, params?: Record<string, string | number>): string {
  return translateWith(key, current, params);
}
